import './ui/scss/app.scss';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import 'react-toastify/dist/ReactToastify.css';
import {
  Router, Route, Switch, Redirect
} from 'react-router-dom';

import 'moment/locale/it';
import 'moment/locale/ar';
import 'moment/locale/ro';
import 'moment/locale/sq';
import 'moment/locale/hu';
import 'moment/locale/sr';
import 'moment/locale/hr';
import 'moment/locale/sl';
import 'moment/locale/zh-cn';
import 'moment/locale/bs';
import 'moment/locale/sk';
import 'moment/locale/uk';
import maintenanceStatus from './maintenance-status.json';

import './ui/scss/bootstrap/bootstrap-grid.min.css';

import moment from 'moment';
import theme from './ui/theme';
import routes from './routes';
import { history } from './app/redux/store';

import { GlobalLayout, Loader } from './ui/components';

/* Pages */
import {
  Auth, AuthReset, Home, Search, Faq,
  Notifications, LearningEvents, Toj,
  TojSurvey, RemoteClassroomSurvey, Maintenance,
  MandatoryTraining, OptionalCourses, Academy, MandatoryTrainingDetails,
  Favourites, OptionalCoursesDetails, AcademyDetails
} from './app/pages';
import MaintenancePage from './ui/components/Maintenance/MaintenancePage';
import { APP_STORE_RELOAD } from './app/redux/actions';
import { ModalsPortal } from './ui/components/Modals';
import ErrorModal from './ui/components/Modals/ErrorModal';
import RemoteClassroom from './app/pages/RemoteClassroom';
import TojSurveySession from './app/pages/TojSurveySession';
import storage from './utils/storage';
import { shouldUseMaintenance } from './utils/common';
import { getCountry, getDefaultLanguage } from './utils/locale';
import Toast from './ui/components/Toast';

const App = ({ appStoreReload, isLogged }) => {
  const { defaultLanguage, isInStorage } = getDefaultLanguage();
  moment.locale(defaultLanguage !== 'zh-hans' ? defaultLanguage : 'zh-cn');
  !isInStorage && storage.write('default_language', defaultLanguage);

    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [checkingMaintenance, setCheckingMaintenance] = useState(true);

    useEffect(() => {
        checkMaintenanceStatus();
    }, []);

    const checkMaintenanceStatus = () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const bypassToken = urlParams.get('access');

            if (bypassToken) {
                storage.write('maintenance_bypass', bypassToken);
                localStorage.setItem('maintenance_bypass', bypassToken);
                window.history.replaceState({}, document.title, window.location.pathname);
            }

            let storedToken = storage.read('maintenance_bypass')
                || localStorage.getItem('maintenance_bypass')
                || sessionStorage.getItem('maintenance_bypass');

            const data = maintenanceStatus;

            const now = new Date();
            const startDate = data.startDate ? new Date(data.startDate) : null;
            const endDate = data.endDate ? new Date(data.endDate) : null;

            const isAfterStart = !startDate || now >= startDate;
            const isBeforeEnd = !endDate || now <= endDate;

            const maintenanceActive = data.enabled === true && isAfterStart && isBeforeEnd;

            setMaintenanceMode(maintenanceActive);

            if (maintenanceActive) {
                setHasAccess(storedToken === data.bypassToken);
            } else {
                setHasAccess(true);
            }

        } catch (err) {
            console.error('Errore nel controllo manutenzione:', err);
            setMaintenanceMode(false);
            setHasAccess(true);
        } finally {
            setCheckingMaintenance(false);
        }
    };

  useEffect(() => {
    function populateStorage() {
      const { value } = getCountry();
      storage.write('default_language', value);
      window.location.reload();
    }
    window.addEventListener('storage', populateStorage);
    return () => window.removeEventListener('storage', populateStorage);
  }, []);

  useEffect(() => {
    appStoreReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    if (checkingMaintenance) {
        return (
            <ThemeProvider theme={theme('themeBlack')}>
                <Loader />
            </ThemeProvider>
        );
    }

    if (maintenanceMode && !hasAccess) {
        return (
            <ThemeProvider theme={theme('themeBlack')}>
                <MaintenancePage />
            </ThemeProvider>
        );
    }

  return (
    <ThemeProvider theme={theme('themeBlack')}>
      <Router history={history}>
          {isLogged ? (
          <GlobalLayout>
            <Switch>
              <Route
                path={routes.home.path}
                exact
                render={() => <Home />}
              />
              <Route
                path={routes.notifications.path}
                render={() => <Notifications history={history} />}
                exact
              />
              <Route
                path={routes.favourites.path}
                render={() => <Favourites />}
                exact
              />

              {/* Mandatory Training */}
              <Route
                path={`${routes.mandatorytraining.path}/:course_id/:lo_id`}
                render={({ match: { params: { course_id = '', lo_id = '' } = {} } = {} }) => (
                  <MandatoryTrainingDetails course_id={course_id} lo_id={lo_id} />
                )}
                exact
              />
              <Route
                path={`${routes.mandatorytraining.path}/:course_id`}
                render={({ match: { params: { course_id = '' } = {} } = {} }) => (
                  <MandatoryTrainingDetails course_id={course_id} />
                )}
                exact
              />
              <Route
                path={routes.mandatorytraining.path}
                render={() => <MandatoryTraining />}
                exact
              />

              {/* Optional Courses */}
              <Route
                path={`${routes.optionalcourses.path}/:course_id/:lo_id`}
                render={({ match: { params: { course_id = '', lo_id = '' } = {} } = {} }) => (
                  <OptionalCoursesDetails course_id={course_id} lo_id={lo_id} />
                )}
                exact
              />
              <Route
                path={`${routes.optionalcourses.path}/:course_id`}
                render={({ match: { params: { course_id = '' } = {} } = {} }) => (
                  <OptionalCoursesDetails course_id={course_id} />
                )}
                exact
              />
              <Route
                path={routes.optionalcourses.path}
                render={() => <OptionalCourses />}
                exact
              />

              {/* Academy Details (DEVE VENIRE PRIMA DEL LISTING) */}
              <Route
                path={`${routes.academy.path}/:course_id/:lo_id`}
                render={({ match: { params: { course_id = '', lo_id = '' } = {} } = {} }) => (
                  <AcademyDetails course_id={course_id} lo_id={lo_id} />
                )}
                exact
              />
              <Route
                path={`${routes.academy.path}/:course_id`}
                render={({ match: { params: { course_id = '' } = {} } = {} }) => (
                  <AcademyDetails course_id={course_id} />
                )}
                exact
              />
              {/* Academy listing */}
              <Route
                path={routes.academy.path}
                render={() => <Academy />}
                exact
              />

              {/* Learning Events */}
              <Route
                path={routes.learningevents.path}
                render={() => <LearningEvents />}
                exact
              />

              {/* Remote Classroom */}
              <Route
                path={`${routes.remoteclassroom.path}/:id`}
                render={({ match: { params: { id = '' } = {} } = {} }) => (
                  <RemoteClassroom id={id} />
                )}
                exact
              />
              <Route
                path={`${routes.remoteclassroom.path}/:id/survey`}
                render={({ match: { params: { id = '' } = {} } = {} }) => (
                  <RemoteClassroomSurvey remoteClassroomId={id} />
                )}
                exact
              />

              {/* TOJ */}
              <Route
                path={`${routes.toj.path}/:id`}
                render={({ match: { params: { id = '' } = {} } = {} }) => (
                  <Toj tojId={id} />
                )}
                exact
              />
              <Route
                path={`${routes.toj.path}/:id/survey`}
                render={({ match: { params: { id = '' } = {} } = {} }) => (
                  <TojSurvey tojId={id} />
                )}
                exact
              />
              <Route
                path={`${routes.toj.path}/:id/surveySession`}
                render={({ match: { params: { id = '' } = {} } = {} }) => (
                  <TojSurveySession tojId={id} />
                )}
                exact
              />

              {/* Misc */}
              <Route path={routes.faq.path} render={() => <Faq />} exact />
              <Route
                path={`${routes.search.path}`}
                exact
                render={props => <Search {...props} title={routes.search.title} />}
              />

              <Redirect to={routes.home.path} />
            </Switch>
          </GlobalLayout>
        ) : (
          <>
            {!shouldUseMaintenance() ? (
              <Switch>
                <Route path={routes.resetAuth.path} render={() => <AuthReset />} />
                <Route path={routes.auth.path} render={() => <Auth />} />
                <Redirect to={routes.auth.path} />
              </Switch>
            ) : (
              <Switch>
                <Route path={`${routes.maintenance.path}`} exact render={() => <Maintenance />} />
                <Redirect to={routes.maintenance.path} />
              </Switch>
            )}
          </>
        )}
        <Loader />
      </Router>
      <ErrorModal />
      <Toast />
      <ModalsPortal.Target />
    </ThemeProvider>
  );
};

App.propTypes = {
  isLogged: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  user_default_language: PropTypes.string,
  appStoreReload: PropTypes.func.isRequired
};

export default connect(
  state => {
    const { isLogged } = state.app.authState;
    const { location } = state.router;
    return { isLogged, location };
  },
  dispatch => ({
    appStoreReload: () => dispatch({ type: APP_STORE_RELOAD })
  })
)(App);
